import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import {
  useClearOperatorContext,
  useOperatorInstitutionSwitch,
} from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import { usePlatformMode } from '@backrs_ui/grow-ui/src/hooks/usePlatformMode';
import {
  applyParamUpdates,
  DASHBOARD_PARAM,
  parseIntParam,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/dashboardUrlParams';
import {
  useGetOperatorContextQuery,
  useGetOperatorFleetQuery,
  type MatchedInstitution,
  type OperatorFleetSort,
  type OperatorRow,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { skipToken } from '@reduxjs/toolkit/query';

import { ROSTERS_ADMIN_PARAM, ROSTERS_ADMIN_TAB } from './rostersAdminTabs';

export const OPERATORS_PER_PAGE = 25;

const SEARCH_DEBOUNCE_MS = 300;

const NAME_ASC: OperatorFleetSort = { field: 'name', direction: 'ASC' };
const NAME_DESC: OperatorFleetSort = { field: 'name', direction: 'DESC' };

export type OperatorsTabControllerArgs = Readonly<{
  enabled: boolean;
}>;

export const useOperatorsTabController = ({ enabled }: OperatorsTabControllerArgs) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const switchOperatorInstitution = useOperatorInstitutionSwitch();
  const onClearOperator = useClearOperatorContext();
  const platform = usePlatformMode({ enabled });

  const urlSearch = searchParams.get(DASHBOARD_PARAM.q) ?? '';
  const page = Math.max(0, parseIntParam(searchParams.get(DASHBOARD_PARAM.page), 0));

  const [draft, setDraft] = useState(urlSearch);
  const [direction, setDirection] = useState<'ASC' | 'DESC'>('ASC');
  /** The last term this controller put in the URL. It tells a write of its own apart from
   * one that arrived from outside — a history pop, or a link — which is what lets the two
   * directions run without chasing each other. */
  const lastWrittenQRef = useRef(urlSearch);
  // Trimmed once, here: the read filters on the trimmed term, so a draft of spaces that
  // reached the URL and the empty-search wording would describe a search never sent.
  const search = useDebounce(draft, SEARCH_DEBOUNCE_MS).trim();

  const updateParams = useCallback(
    (updates: Record<string, string | number | null>) => {
      setSearchParams((prev) => applyParamUpdates(prev, updates), { replace: true });
    },
    [setSearchParams],
  );

  // The debounced term is what was searched for; the URL mirrors it so returning to the tab
  // comes back to the same list. A new term restarts the paging it was not read under.
  useEffect(() => {
    if (search === lastWrittenQRef.current) return;

    lastWrittenQRef.current = search;
    updateParams({
      [DASHBOARD_PARAM.q]: search === '' ? null : search,
      [DASHBOARD_PARAM.page]: null,
    });
  }, [search, updateParams]);

  // The other direction: a q this controller did not write came from outside it, so the
  // field follows the URL. Without this the draft outlives a history pop and keeps
  // rewriting the term the operator just navigated away from.
  useEffect(() => {
    if (urlSearch === lastWrittenQRef.current) return;

    lastWrittenQRef.current = urlSearch;
    setDraft(urlSearch);
  }, [urlSearch]);

  const args = useMemo(
    () => ({
      search,
      range: [
        page * OPERATORS_PER_PAGE,
        page * OPERATORS_PER_PAGE + OPERATORS_PER_PAGE - 1,
      ] as const,
      sort: direction === 'ASC' ? NAME_ASC : NAME_DESC,
    }),
    [direction, page, search],
  );

  const fleet = useGetOperatorFleetQuery(enabled ? args : skipToken);
  const context = useGetOperatorContextQuery(enabled ? undefined : skipToken);

  const onPageChange = useCallback(
    (next: number) => updateParams({ [DASHBOARD_PARAM.page]: next === 0 ? null : next }),
    [updateParams],
  );

  const total = fleet.data?.total ?? null;

  // The page index outlives the fleet it indexes into, and this read pages on the server, so
  // an index past the end answers an empty window beside a non-zero total — which would draw
  // the empty-fleet wording next to a control still offering the page. Clamped against the
  // answer rather than the request, so it cannot fire before the read lands, and not at all
  // where the fleet size is unknown.
  useEffect(() => {
    if (!fleet.isSuccess || fleet.isFetching || total === null) return;

    const lastPage = Math.max(0, Math.ceil(total / OPERATORS_PER_PAGE) - 1);

    if (page > lastPage) onPageChange(lastPage);
  }, [fleet.isFetching, fleet.isSuccess, onPageChange, page, total]);

  const onToggleSort = useCallback(() => {
    setDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    onPageChange(0);
  }, [onPageChange]);

  /** The switch and the landing are one navigation: two would leave the second composing
   * off the params the first had already replaced. */
  const onOpenOperator = useCallback(
    (operator: OperatorRow) => {
      switchOperatorInstitution(operator.id, {
        [ROSTERS_ADMIN_PARAM.tab]: ROSTERS_ADMIN_TAB.COHORTS,
      });
    },
    [switchOperatorInstitution],
  );

  const onOpenMatchedInstitution = useCallback(
    (operator: OperatorRow, institution: MatchedInstitution) => {
      switchOperatorInstitution(operator.id, {
        [ROSTERS_ADMIN_PARAM.tab]: ROSTERS_ADMIN_TAB.COHORTS,
        [DASHBOARD_PARAM.institutionId]: institution.id,
      });
    },
    [switchOperatorInstitution],
  );

  return {
    draft,
    onDraftChange: setDraft,
    sortDirection: direction,
    onToggleSort,
    page,
    onPageChange,
    rowsPerPage: OPERATORS_PER_PAGE,
    rows: fleet.data?.data ?? [],
    total,
    dropped: fleet.data?.dropped ?? 0,
    isLoading: fleet.isFetching,
    isError: fleet.isError,
    errorMessage: refusalProse(fleet.error),
    /** Narrowed away rather than never read: an empty page under a search is not an empty fleet. */
    isSearching: search !== '',
    isTotalKnown: total !== null,
    onOpenOperator,
    onOpenMatchedInstitution,
    home: context.data?.home ?? null,
    /** A read that has not landed or did not land is a different answer from a caller with
     * no home: without them the only way back to the home context disappears while the read
     * is in flight and again if it fails. */
    isContextError: context.isError,
    isContextLoading: context.isLoading,
    platformAvailable: platform.available,
    onEnterPlatform: platform.enter,
    onClearOperator,
  };
};
